import {
  CoverPageOfferFragment,
  OfferAccessState
} from "@pathwright/graphql-client/src/generated"
import type { RegistrationAccessRow } from "../registration/access"
import {
  isCohortStoreOffer,
  offerAllowsCreateAnother
} from "./coverPageOfferOrdering"

/**
 * Secondary Continue action when the user already has access but can still
 * join and/or create via Available Offers on this Cover Page.
 */
export type CoverPageAnotherActionKind =
  | "none"
  | "join_another"
  | "create_one"
  | "create_another"
  | "join_or_create"
  | "join_or_create_another"

export type CoverPageAnotherAction = {
  kind: CoverPageAnotherActionKind
  show: boolean
}

export type CoverPageAnotherActionLabels = {
  ellipsisKey: string
  ellipsisDefault: string
  questionKey: string
  questionDefault: string
}

/** True when the user has created a Private Cohort on this resource. */
export function hasCreatedPrivateCohort(
  accesses: RegistrationAccessRow[]
): boolean {
  return accesses.some(
    (row) => row.is_creator && row.resource_license_offering_id != null
  )
}

/** Available Join Cohort Offers the user can still take. */
export function offersAllowJoinAnother(
  offers: CoverPageOfferFragment[] | null | undefined
): boolean {
  return (
    offers?.some(
      (offer) =>
        isCohortStoreOffer(offer) &&
        offer.access.state === OfferAccessState.Available
    ) ?? false
  )
}

/** Available Private Cohort Offers the user can still create from. */
export function offersAllowCreate(
  offers: CoverPageOfferFragment[] | null | undefined
): boolean {
  return offers?.some((offer) => offerAllowsCreateAnother(offer)) ?? false
}

/**
 * Resource-level secondary action for the header Continue menu / under-link.
 * Driven by Available Offers + whether the user has created a Private Cohort.
 */
export function getCoverPageAnotherAction({
  offers,
  accesses
}: {
  offers: CoverPageOfferFragment[] | null | undefined
  accesses: RegistrationAccessRow[]
}): CoverPageAnotherAction {
  const canJoin = offersAllowJoinAnother(offers)
  const canCreate = offersAllowCreate(offers)
  if (!canJoin && !canCreate) {
    return { kind: "none", show: false }
  }

  const hasCreated = hasCreatedPrivateCohort(accesses)

  if (canJoin && canCreate) {
    return {
      kind: hasCreated ? "join_or_create_another" : "join_or_create",
      show: true
    }
  }

  if (canJoin) {
    return { kind: "join_another", show: true }
  }

  return {
    kind: hasCreated ? "create_another" : "create_one",
    show: true
  }
}

/**
 * Private Cohort (or create-capable) Offer card secondary action — create-only
 * language. Join language belongs on the resource-level header action.
 */
export function getPrivateCohortAnotherAction({
  offer,
  accesses
}: {
  offer: CoverPageOfferFragment
  accesses: RegistrationAccessRow[]
}): CoverPageAnotherAction {
  if (!offerAllowsCreateAnother(offer)) {
    return { kind: "none", show: false }
  }

  return {
    kind: hasCreatedPrivateCohort(accesses) ? "create_another" : "create_one",
    show: true
  }
}

const LABELS: Record<
  Exclude<CoverPageAnotherActionKind, "none">,
  CoverPageAnotherActionLabels
> = {
  join_another: {
    ellipsisKey: "offer.join_another_ellipsis",
    ellipsisDefault: "Join another…",
    questionKey: "offer.join_another_question",
    questionDefault: "Join another?"
  },
  create_one: {
    ellipsisKey: "offer.create_one_ellipsis",
    ellipsisDefault: "Create one…",
    questionKey: "offer.create_one_question",
    questionDefault: "Create one?"
  },
  create_another: {
    ellipsisKey: "offer.create_another_ellipsis",
    ellipsisDefault: "Create another…",
    questionKey: "offer.create_another_question",
    questionDefault: "Create another?"
  },
  join_or_create: {
    ellipsisKey: "offer.join_or_create_ellipsis",
    ellipsisDefault: "Join or create…",
    questionKey: "offer.join_or_create_question",
    questionDefault: "Join or create?"
  },
  join_or_create_another: {
    ellipsisKey: "offer.join_or_create_another_ellipsis",
    ellipsisDefault: "Join or create another…",
    questionKey: "offer.join_or_create_another_question",
    questionDefault: "Join or create another?"
  }
}

export function getCoverPageAnotherActionLabels(
  kind: CoverPageAnotherActionKind
): CoverPageAnotherActionLabels | null {
  if (kind === "none") return null
  return LABELS[kind]
}
